import React from 'react';
import { StripeProvider, Elements } from 'react-stripe-elements';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { ClipLoader } from 'react-spinners';
import i18n from '../utils/i18n';
import StripeForm from '../components/StripeForm';
import ApiClient from '../utils/apiClient';
import Button from '../components/Button';
import { getUserData, userUpdate } from '../actions/userActions';
import { openModal } from '../actions/modalsActions';
import {
  modals,
  subscriptionStatuses,
  paymentStatuses
} from '../utils/constants';
import { setPaymentStatus, checkIfCardIsNew } from '../actions/paymentActions';

import './styles/StripeFormContainer.scss';

const disableButtonStatuses = [
  paymentStatuses.STRIPE_CHECKOUT,
  paymentStatuses.PROCESS_PAYMENT,
  paymentStatuses.PROCESSING_IN_PROGRESS
];

class StripeFormContainer extends React.Component {
  constructor(props) {
    super(props);
    this.handleStripeFormSubmit = this.handleStripeFormSubmit.bind(this);
  }

  componentDidMount() {
    this.props.getUserDataFunc();
    this.props.setPaymentStatus(paymentStatuses.START);
    if (!this.props.isUpdate) {
      this.checkIfPaymentDone();
    }
  }

  componentDidUpdate() {
    if (!this.props.isUpdate) {
      this.checkIfPaymentDone();
    }

    switch (this.props.paymentStatus) {
    case paymentStatuses.PROCESS_PAYMENT:
      this.props.setPaymentStatus(paymentStatuses.PROCESSING_IN_PROGRESS);
      this.processCard(this.props.token);
      break;
    default:
      break;
    }
  }

  getErrorMessage() {
    switch (this.props.paymentStatus) {
    case paymentStatuses.STRIPE_CHECKOUT_FAILED:
    case paymentStatuses.PAYMENT_FAILED:
      return i18n.t('payments.ERROR');
    default:
      return '';
    }
  }

  processCard(token) {
    const apiUrl = this.props.isUpdate
      ? '/api/v1/update-subscription'
      : '/api/v1/payment/';

    this.props.userUpdate({
      processing_card_data: true
    });
    return ApiClient.post(apiUrl, {
      source_token: token
    })
      .then(() => {
        if (this.props.isUpdate) {
          this.props.openModal(modals.UPDATE_CARD_SUCCESS, {});
        }
      })
      .catch(() => {
        this.props.setPaymentStatus(paymentStatuses.PAYMENT_FAILED);
      })
      .finally(() => {
        this.props.getUserDataFunc();
      });
  }

  checkIfPaymentDone() {
    const { user } = this.props;
    if (
      user &&
      user.subscription_status !== subscriptionStatuses.NO_VALID_SUBSCRIPTION
    ) {
      this.props.openModal(modals.PAYMENT_SUCCESS, {});
    }
  }

  handleStripeFormSubmit(stripe, ev) {
    ev.preventDefault();

    if (stripe) {
      this.props.setPaymentStatus(paymentStatuses.STRIPE_CHECKOUT);
      stripe
        .createToken()
        .then(data => {
          const tokenId = data.token && data.token.id;
          if (this.props.isUpdate) {
            this.props.setPaymentStatus(
              paymentStatuses.PROCESS_PAYMENT,
              tokenId
            );
          } else {
            this.props.checkIfCardIsNew(tokenId);
          }
        })
        .catch(() => {
          this.props.setPaymentStatus(paymentStatuses.STRIPE_CHECKOUT_FAILED);
        });
    } else {
      this.props.setPaymentStatus(paymentStatuses.STRIPE_CHECKOUT_FAILED);
    }
  }

  render() {
    const { config, user, getUserDataFunc } = this.props;
    const stripeApiKey = config && config.stripe_public_api_key;
    const processingCardData = user && user.processing_card_data;

    const buttonDisabled =
      disableButtonStatuses.indexOf(this.props.paymentStatus) !== -1;

    if (stripeApiKey && user) {
      if (!processingCardData) {
        return (
          <div className="stripe-provider-container">
            <StripeProvider apiKey={ stripeApiKey }>
              <Elements>
                <StripeForm
                  handleSubmit={ this.handleStripeFormSubmit }
                  buttonDisabled={ buttonDisabled }
                  buttonText={
                    this.props.isUpdate
                      ? i18n.t('payments.UPDATE')
                      : i18n.t('payments.BUY')
                  }
                />
              </Elements>
            </StripeProvider>
            <div className="stripe-error-message">
              {this.getErrorMessage()}
            </div>
          </div>
        );
      }
      return (
        <div className="stripe-processing-payment">
          {this.props.isUpdate
            ? i18n.t('payments.PROCESING_UPDATE')
            : i18n.t('payments.PROCESING_PAYMENT')}
          <div className="loader">
            <ClipLoader color="#5c84fe" loading={ true } size={ 50 } />
          </div>
          <div className="refresh-button">
            <Button text="Refresh" onClick={ getUserDataFunc } />
          </div>
        </div>
      );
    }
    return (
      <strong>
        {i18n.t('payments.PREPARING_PAYMENT')}
      </strong>
    );
  }
}

StripeFormContainer.propTypes = {
  config: PropTypes.shape({
    stripe_public_api_key: PropTypes.string
  }),
  user: PropTypes.shape({
    processing_card_data: PropTypes.bool
  }),
  getUserDataFunc: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  userUpdate: PropTypes.func.isRequired,
  isUpdate: PropTypes.bool,
  checkIfCardIsNew: PropTypes.func.isRequired,
  setPaymentStatus: PropTypes.func.isRequired,
  paymentStatus: PropTypes.string,
  token: PropTypes.string
};

StripeFormContainer.defaultProps = {
  config: {
    stripe_public_api_key: ''
  },
  user: {
    processing_card_data: false
  },
  isUpdate: false,
  paymentStatus: '',
  token: ''
};

const mapStateToProps = state => ({
  config: state.config.data,
  user: state.auth.user,
  paymentStatus: state.payment.paymentStatus,
  token: state.payment.token
});

const mapDispatchToProps = dispatch => ({
  getUserDataFunc: () => dispatch(getUserData()),
  openModal: (modal, modalData) => dispatch(openModal(modal, modalData)),
  userUpdate: userData => dispatch(userUpdate(userData)),
  checkIfCardIsNew: token => dispatch(checkIfCardIsNew(token)),
  setPaymentStatus: (paymentStatus, token) =>
    dispatch(setPaymentStatus(paymentStatus, token))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(StripeFormContainer);



// WEBPACK FOOTER //
// ./src/containers/StripeFormContainer.js